import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row
} from 'antd'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import {
  useClassifierServiceCreateReferenceIntervalClassifierElementEndpoint,
  useClassifierServiceGetReferenceIntervalClassifierElementEndpoint,
  useClassifierServiceGetReferenceIntervalClassifierElementEndpointKey,
  useClassifierServiceGetReferenceIntervalClassifierElementListEndpointKey,
  useClassifierServiceUpdateReferenceIntervalClassifierElementEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { EditableFormItem, message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId
      ? t('laboratory_classifiers.edit_option')
      : t('laboratory_classifiers.add_option'),
    button: hasId ? t('common.edit') : t('common.save')
  }
}

export const ReferenceIntervalModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const { button, title } = getModalText(!!id, t)

  const { data } =
    useClassifierServiceGetReferenceIntervalClassifierElementEndpoint(
      {
        id: String(id)
      },
      [id],
      {
        enabled: !!id
      }
    )

  const { mutate: create, isPending: isCreating } =
    useClassifierServiceCreateReferenceIntervalClassifierElementEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('laboratory_classifiers.create_success_option'))
        onMutationSuccess()
      }
    })

  const { mutate: edit, isPending: isEditing } =
    useClassifierServiceUpdateReferenceIntervalClassifierElementEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(t('laboratory_classifiers.edit_success_option'))
        onMutationSuccess()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const { maxValue, minValue } = values

    if (!minValue && !maxValue) {
      form.setFields([
        {
          name: 'minValue',
          errors: [t('laboratory_classifiers.min_max_value_error')]
        },
        {
          name: 'maxValue',
          errors: [t('laboratory_classifiers.min_max_value_error')]
        }
      ])
      return
    }

    if (minValue && maxValue && minValue > maxValue) {
      form.setFields([
        {
          name: 'minValue',
          errors: [t('laboratory_classifiers.min_value_error')]
        }
      ])
      return
    }

    return id
      ? edit({
          id: String(id),
          requestBody: values
        })
      : create({ requestBody: values })
  }

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [
        useClassifierServiceGetReferenceIntervalClassifierElementListEndpointKey
      ]
    })
    queryClient.invalidateQueries({
      queryKey: [
        useClassifierServiceGetReferenceIntervalClassifierElementEndpointKey
      ]
    })
    onCancel()
  }

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data)
    }
  }, [data])

  return (
    <Modal
      open
      centered
      title={title}
      maskClosable={false}
      footer={false}
      onCancel={onCancel}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['translation', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={12}>
            <Form.Item
              name="minValue"
              label={t('laboratory_classifiers.min_value')}
            >
              <InputNumber className="full-width" min={0} placeholder="0" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="maxValue"
              label={t('laboratory_classifiers.max_value')}
            >
              <InputNumber className="full-width" min={0} placeholder="0" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber className="full-width" min={0} placeholder="0" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea placeholder={t('common.comments')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${button}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isEditing || isCreating }}
              >
                <Button type="primary">{button}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
